/**
 * Helper utilities for calculating pawn movement paths and destinations
 *
 * Provides utilities to determine where a pawn will land after moving
 * a specific number of positions on the board.
 */

import type { PawnPosition } from '../../types/positions';
import type { BoardPosition } from '../../components/board/constants/positions';
import type { PlayerColor } from 'shared-types';

/**
 * Section configuration for board circuit
 * Defines the order of positions in each section's roads
 */
const SECTION_PATHS = {
  G: {
    start: ['GS1', 'GS2', 'GS3', 'GS4', 'GS5', 'GS6'],
    final: ['GF6', 'GF5', 'GF4', 'GF3', 'GF2', 'GF1'],
    center: ['GC1', 'GC2', 'GC3', 'GC4', 'GC5', 'GC6'],
    finalPosition: 'GFINAL'
  },
  Y: {
    start: ['YS1', 'YS2', 'YS3', 'YS4', 'YS5', 'YS6'],
    final: ['YF6', 'YF5', 'YF4', 'YF3', 'YF2', 'YF1'],
    center: ['YC1', 'YC2', 'YC3', 'YC4', 'YC5', 'YC6'],
    finalPosition: 'YFINAL'
  },
  B: {
    start: ['BS1', 'BS2', 'BS3', 'BS4', 'BS5', 'BS6'],
    final: ['BF6', 'BF5', 'BF4', 'BF3', 'BF2', 'BF1'],
    center: ['BC1', 'BC2', 'BC3', 'BC4', 'BC5', 'BC6'],
    finalPosition: 'BFINAL'
  },
  R: {
    start: ['RS1', 'RS2', 'RS3', 'RS4', 'RS5', 'RS6'],
    final: ['RF6', 'RF5', 'RF4', 'RF3', 'RF2', 'RF1'],
    center: ['RC1', 'RC2', 'RC3', 'RC4', 'RC5', 'RC6'],
    finalPosition: 'RFINAL'
  }
} as const;

/**
 * Section order for clockwise traversal around the board
 */
const SECTION_ORDER = ['G', 'Y', 'B', 'R'] as const;

/**
 * Map player colors to pawn color prefixes used in position IDs
 * Centralizes color mapping logic for reuse across the application
 */
export const PLAYER_COLOR_TO_PREFIX: Record<PlayerColor, 'G' | 'Y' | 'B' | 'R'> = {
  green: 'G',
  yellow: 'Y',
  blue: 'B',
  red: 'R'
} as const;

/**
 * Regular expression pattern for center corridor positions (XC1-XC6)
 * Matches positions like GC1, YC3, BC6, RC2 etc.
 */
const CENTER_CORRIDOR_PATTERN = /^[GYBR]C[1-6]$/;

/**
 * Check if a position is in the center corridor (XC1-XC6)
 * These are the positions where exact roll is required to reach XFINAL.
 *
 * Note: XFINAL positions are NOT included as they are the destination, not corridor.
 *
 * @param position - The board position to check
 * @returns True if position is in center corridor (XC1-XC6)
 */
export function isInCenterCorridor(position: BoardPosition): boolean {
  return CENTER_CORRIDOR_PATTERN.test(position);
}

/**
 * Build the complete forward path for a given color starting from their start position
 * This creates the circuit path that a pawn follows from start to final
 *
 * @param colorPrefix - The color section prefix ('G', 'Y', 'B', or 'R')
 * @returns Array of all positions in order that a pawn will traverse
 */
function buildForwardPath(colorPrefix: 'G' | 'Y' | 'B' | 'R'): BoardPosition[] {
  const path: BoardPosition[] = [];
  const startSectionIndex = SECTION_ORDER.indexOf(colorPrefix);

  // Add positions for the current section's start road (from S2 onwards, as S1 is the entry point from C1)
  const homeSection = SECTION_PATHS[colorPrefix];
  path.push(...([...homeSection.start.slice(1)] as BoardPosition[])); // S2 to S6

  // Traverse through the next 3 sections in clockwise order
  for (let i = 1; i <= 3; i++) {
    const sectionIndex = (startSectionIndex + i) % 4;
    const section = SECTION_PATHS[SECTION_ORDER[sectionIndex]];

    // Add circuit through this section: F6 -> F5 -> ... -> F1 -> C1 -> S1 -> S2 -> ... -> S6
    // Include C1 as transition point, but skip C2-C6 (only for home section)
    path.push(...([...section.final] as BoardPosition[])); // F6 to F1
    path.push(section.center[0] as BoardPosition); // C1 (transition point)
    path.push(...([...section.start] as BoardPosition[])); // S1 to S6
  }

  // After completing the circuit, enter home section's final road
  path.push(...([...homeSection.final] as BoardPosition[])); // F6 to F1

  // Then enter center path to final position
  path.push(homeSection.center[0] as BoardPosition); // C1
  path.push(...([...homeSection.center.slice(1)] as BoardPosition[])); // C2 to C6
  path.push(homeSection.finalPosition as BoardPosition); // FINAL

  return path;
}

/**
 * Pre-built path cache for each color
 * Paths are built once at module initialization for O(1) lookup performance
 */
const PATH_CACHE: Record<'G' | 'Y' | 'B' | 'R', BoardPosition[]> = {
  G: buildForwardPath('G'),
  Y: buildForwardPath('Y'),
  B: buildForwardPath('B'),
  R: buildForwardPath('R')
} as const;

/**
 * Calculate the destination position after moving a certain number of steps
 * from the current position
 *
 * @param currentPosition - The current position of the pawn
 * @param steps - Number of steps to move (dice value)
 * @param pawnColor - The home color of the pawn ('G', 'Y', 'B', or 'R')
 * @returns The destination position after moving, or null if the move would overshoot XFINAL from center corridor
 * @throws Error if current position is invalid (not found in path)
 */
export function calculateDestination(
  currentPosition: PawnPosition,
  steps: number,
  pawnColor: 'G' | 'Y' | 'B' | 'R'
): PawnPosition | null {
  // Use pre-built path cache for O(1) lookup instead of rebuilding
  const forwardPath = PATH_CACHE[pawnColor];

  // Find current position in the path
  const currentIndex = forwardPath.indexOf(currentPosition);

  if (currentIndex === -1) {
    throw new Error(
      `Invalid position: ${currentPosition} not found in path for color ${pawnColor}`
    );
  }

  // Calculate destination index
  const destinationIndex = currentIndex + steps;

  // Check if destination is within bounds
  if (destinationIndex >= forwardPath.length) {
    // If current position is in center corridor, return null (overshoot - pawn blocked)
    // This implements the exact roll mechanic for reaching XFINAL
    if (isInCenterCorridor(currentPosition)) {
      return null;
    }

    // For positions outside center corridor, this is an invalid move
    throw new Error(
      `Cannot move ${steps.toString()} steps from ${currentPosition}: would exceed final position`
    );
  }

  return forwardPath[destinationIndex];
}
